/**
 * THE ROW AMOUNT CHIP, AND WHY IT IS NOT THE TILE'S NUMBER.
 *
 * Ashok ruling, 2026-09-01. The row shows the BUYER'S OWN CURRENCY -
 * "INR 3,750,000" - comma-separated, no decimals. The TILES show a USD
 * sum. Those are different numbers for the same document, on purpose, and
 * the surfaces must say which is which or the pair reads as an arithmetic
 * error rather than as two honest figures.
 *
 * THE SOURCE IS spend_usd_provenance, NOT estimated_spend_usd.
 * estimated_spend_usd is the CONVERTED figure; the buyer never typed it.
 * The provenance block carries what they did type - {amount, currency,
 * rate_used, rate_effective_date} - and backend/routes/procurement.py's
 * compute_estimated_spend_usd writes both keys TOGETHER or neither, so a
 * row with a converted figure always has its original beside it.
 *
 * A ROW WITH NO AMOUNT MUST READ AS "NOT ASKED YET", NEVER AS ZERO.
 * A document before submit-answers cannot have one - that is where
 * compute_estimated_spend_usd runs - and 186 of 244 staging documents have
 * none. Rendering "0" or "$0" would state a budget of zero for a question
 * nobody has asked. formatEntryAmount returns null and the caller renders
 * a muted dash with a title that says why.
 */

// Intl with no fraction digits. en-US gives the comma grouping the ruling
// asks for; the currency CODE is printed as a plain prefix rather than a
// symbol, so an unfamiliar currency never renders as an ambiguous glyph.
const GROUPED = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const NO_AMOUNT_TITLE = 'No budget captured yet';

/**
 * The buyer's own figure for one procurement, or null when there is none.
 * Never returns "0" for an absent amount - see the header.
 */
export const formatEntryAmount = (procurement) => {
  const provenance = procurement?.spend_usd_provenance;
  if (!provenance) return null;
  const { amount, currency } = provenance;
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  if (typeof currency !== 'string' || !currency.trim()) return null;
  return `${currency.trim()} ${GROUPED.format(Math.round(amount))}`;
};

/**
 * The rate date behind a row's converted figure, or null.
 * resolve_rate uses the newest rate at or before the entry date with NO
 * staleness bound, so this is the only thing that distinguishes a figure
 * converted today from one converted at a year-old rate.
 */
export const entryRateDate = (procurement) =>
  procurement?.spend_usd_provenance?.rate_effective_date || null;

/** The USD total for a tile, comma-separated, no decimals. */
export const formatUsdTotal = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `$${GROUPED.format(Math.round(value))}`;
};

/**
 * "across 34 of 92" - the denominator, stated rather than hidden.
 * A tile that shows a sum without this understates silently whenever any
 * document in the group has no amount, which is the common case.
 */
export const formatDenominator = (withAmount, count) =>
  `across ${GROUPED.format(withAmount || 0)} of ${GROUPED.format(count || 0)}`;
